import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = 'https://atacodehouse.com/ptd-api/';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getCookie('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth error flag to prevent multiple toasts
let isAuthErrorShowing = false;

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if it's an authentication error (401 Unauthorized)
    const isAuthError = error.response?.status === 401;
    
    // Check if it's a login request
    const isLoginRequest = error.config?.url?.includes('/account/login');
    
    // For login page errors (like wrong password), just return the error
    if (isLoginRequest) {
      return Promise.reject(error);
    }
    
    // For other pages, if it's an auth error (token missing/expired)
    if (isAuthError && !isAuthErrorShowing) {
      isAuthErrorShowing = true;
      
      // Clear the invalid token
      document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      
      // Show message
      toast.error('Session expired. Please login again.', {
        duration: 3000,
        position: 'top-center',
      });
      
      // Dispatch custom event for App.js to handle
      window.dispatchEvent(new CustomEvent('auth-error'));
      
      // Reset flag after 3 seconds
      setTimeout(() => {
        isAuthErrorShowing = false;
      }, 3000);
    }
    
    return Promise.reject(error);
  }
);

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const accountAPI = {
  login: (email, password) => api.post('/account/login', { email, password }),
  
  getMe: () => api.get('/account/me'),
  
  update: (data) => {
    console.log('Sending account update with data:', data);
    
    const token = getCookie('token');
    
    // Use fetch directly to see exact response
    return fetch(`${API_BASE_URL}/account/update`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(async response => {
      const result = await response.json();
      console.log('Account update response:', result);
      return { data: result };
    })
    .catch(error => {
      console.error('Account update error:', error);
      throw error;
    });
  },
};

// Product APIs
export const productAPI = {
  list: () => api.get('/product/list'),
  
  get: (id) => api.get(`/product/${id}`),
  
  create: (data) => {
    // Create payload with correct field names
    const payload = {
      code: data.code,
      name: data.name,
      is_stock_based: data.is_stock_based
    };

    // Only include optional fields if they exist and product is not stock-based
    if (data.use_currency !== undefined) {
      payload.use_currency = data.use_currency;
    }
    if (data.api_code !== undefined) {
      payload.api_code = data.api_code;
    }
    if (data.nick_code !== undefined) {
      payload.nick_code = data.nick_code;
    }

    return api.post('/product/create', payload);
  },
  
  update: (productId, updateData) => {
    const token = getCookie('token');
    
    const payload = {
      product_id: parseInt(productId)
    };
    
    if (updateData.code !== undefined) payload.code = updateData.code;
    if (updateData.name !== undefined) payload.name = updateData.name;
    if (updateData.is_stock_based !== undefined) payload.is_stock_based = updateData.is_stock_based;
    if (updateData.is_active !== undefined) payload.is_active = updateData.is_active;
    if (updateData.use_currency !== undefined) payload.use_currency = updateData.use_currency;
    if (updateData.api_code !== undefined) payload.api_code = updateData.api_code;
    if (updateData.nick_code !== undefined) payload.nick_code = updateData.nick_code;
    
    console.log('Update payload:', payload);

    return fetch(`${API_BASE_URL}/product/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return data;
    });
  },
  
  deactivate: (product_id) => api.post('/product/deactivate', { 
    product_id: parseInt(product_id) 
  }),
};

// Based Product Items APIs
export const basedProductItemAPI = {
  list: (productId) => {
    if (!productId) return Promise.reject('Product ID is required');
    return api.get(`/based_product_item/list?product_id=${encodeURIComponent(productId)}`);
  },
  
  get: (id) => api.get(`/based_product_item/${id}`),
  
  create: (data) => {
    const token = getCookie('token');
    
    const payload = {
      product_id: parseInt(data.product_id),
      code: data.code,
      name: data.name,
      item_id: parseInt(data.item_id)
    };

    if (data.coin_price && !isNaN(parseFloat(data.coin_price))) {
      payload.coin_price = parseFloat(data.coin_price);
    }

    return fetch(`${API_BASE_URL}/based_product_item/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  update: (data) => {
    const token = getCookie('token');
    
    const payload = {
      based_item_id: parseInt(data.based_item_id)
    };
    
    if (data.code) payload.code = data.code;
    if (data.name) payload.name = data.name;
    if (data.coin_price && !isNaN(parseFloat(data.coin_price))) {
      payload.coin_price = parseFloat(data.coin_price);
    }
    if (data.item_id) payload.item_id = parseInt(data.item_id);
    
    return fetch(`${API_BASE_URL}/based_product_item/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return data;
    });
  },
  
  delete: (id) => {
    const token = getCookie('token');
    
    return fetch(`${API_BASE_URL}/based_product_item/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ based_item_id: parseInt(id) })
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
};

// Custom Product Items APIs
export const customProductItemAPI = {
  list: (productId) => {
    if (!productId) return Promise.reject('Product ID is required');
    return api.get(`/custom-product-items${productId ? `?product_id=${encodeURIComponent(productId)}` : ''}`);
  },
  
  get: (id) => api.get(`/custom-product-items/${encodeURIComponent(id)}`),
  
  getByCode: (productId, code) => {
    if (!productId || !code) return Promise.reject('Product ID and code are required');
    return api.get(`/custom-product-items/by-code?product_id=${encodeURIComponent(productId)}&code=${encodeURIComponent(code)}`);
  },
  
  create: (data) => {
    const token = getCookie('token');
    
    const payload = {
      product_id: parseInt(data.product_id),
      code: data.code,
      name: data.name
    };

    if (data.reseller_price !== undefined && data.reseller_price !== '') {
      payload.reseller_price = parseFloat(data.reseller_price);
    }
    if (data.user_price !== undefined && data.user_price !== '') {
      payload.user_price = parseFloat(data.user_price);
    }
    if (data.coin_price !== undefined && data.coin_price !== '') {
      payload.coin_price = data.coin_price;
    }
    if (data.based_item_ids !== undefined && Array.isArray(data.based_item_ids)) {
      payload.based_item_ids = data.based_item_ids.map(id => parseInt(id));
    }

    return fetch(`${API_BASE_URL}/custom-product-items`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  update: (id, data) => {
    const token = getCookie('token');
    
    const payload = { ...data };
    if (payload.product_id) payload.product_id = parseInt(payload.product_id);
    if (payload.reseller_price) payload.reseller_price = parseFloat(payload.reseller_price);
    if (payload.user_price) payload.user_price = parseFloat(payload.user_price);
    if (payload.based_item_ids) {
      payload.based_item_ids = payload.based_item_ids.map(id => parseInt(id));
    }

    return fetch(`${API_BASE_URL}/custom-product-items/${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  delete: (id) => {
    const token = getCookie('token');
    
    return fetch(`${API_BASE_URL}/custom-product-items/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ based_item_id: parseInt(id) })
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
};

// Stock Management APIs
export const stockAPI = {
  list: (customItemId, includeUsed = 0) => {
    if (!customItemId) return Promise.reject('Custom item ID is required');
    return api.get(`/stock/list?custom_item_id=${encodeURIComponent(customItemId)}&include_used=${encodeURIComponent(includeUsed)}`);
  },
  
  get: (id) => api.get(`/stock/${encodeURIComponent(id)}`),
  
  create: (data) => {
    const token = getCookie('token');
    
    const payload = {
      custom_item_id: parseInt(data.custom_item_id),
      stock_data: data.stock_data
    };

    return fetch(`${API_BASE_URL}/stock/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  update: (id, data) => {
    const token = getCookie('token');
    
    const payload = {
      stock_id: parseInt(id)
    };
    
    if (data.stock_data !== undefined) payload.stock_data = data.stock_data;
    if (data.is_used !== undefined) payload.is_used = data.is_used;

    return fetch(`${API_BASE_URL}/stock/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  delete: (id) => {
    const token = getCookie('token');
    
    return fetch(`${API_BASE_URL}/stock/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ stock_id: parseInt(id) })
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
};

// Product Fields APIs
export const productFieldAPI = {
  list: (productId) => {
    if (!productId) return Promise.reject('Product ID is required');
    return api.get(`/product-fields/list?product_id=${encodeURIComponent(productId)}`);
  },
  
  get: (id) => api.get(`/product-fields/${encodeURIComponent(id)}`),
  
  create: (data) => {
    const token = getCookie('token');
    
    const payload = {
      product_id: parseInt(data.product_id),
      name: data.name,
      label: data.label,
      type: data.type,
      sort_order: data.sort_order,
      required: data.required
    };

    return fetch(`${API_BASE_URL}/product-fields/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  update: (data) => {
    const token = getCookie('token');
    
    const payload = {
      field_id: parseInt(data.field_id)
    };
    
    if (data.name !== undefined) payload.name = data.name;
    if (data.label !== undefined) payload.label = data.label;
    if (data.type !== undefined) payload.type = data.type;
    if (data.sort_order !== undefined) payload.sort_order = data.sort_order;
    if (data.required !== undefined) payload.required = data.required;

    return fetch(`${API_BASE_URL}/product-fields/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
  
  delete: (id) => {
    const token = getCookie('token');
    
    return fetch(`${API_BASE_URL}/product-fields/delete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ field_id: parseInt(id) })
    })
    .then(async response => {
      const data = await response.json();
      return { data };
    });
  },
};

export default api;